import React from 'react';
import Dropdown from '../ui/Dropdown';
import { TOOLBAR_DISCLOSURE_TRIGGER } from '../ui/Toolbar';
import { MENU_ITEM_NEUTRAL } from '../OrderActionsMenu';
import ListPrintMenuItems from '../ListPrintMenuItems';
import { downloadHireExport } from '../../utils/hireListExport';
import { CSV_LABEL, CSV_MENU_LABEL } from '../../utils/gagCsvExport';
import { buildHireListDoc } from '../../utils/hireListPdf';
import { HIRE_TABLE_TABS } from '../../utils/hireBuckets';

/**
 * The hires Table's page-level `Actions ▾` (2026-09-27) — Department Orders'
 * menu, item for item where the hires have an equivalent: `Export CSV` ·
 * `Download list (PDF)` · `Print all` · `Fit columns to
 * screen`.
 *
 * 🔀 **`Fit columns to screen` ADDED 2026-09-28** (owner ruling: *"reset
 * column widths should use the same code and changes as Fit columns to
 * screen on the Breakdown page. same for other tables"*) — until then this
 * menu carried no width item at all (the hires Table only ever offered a
 * per-column double-click reset, and a menu item for it "was not in the
 * ruling"). The PAGE now owns the width state (`hooks/useColumnWidths.js`,
 * lifted out of `HireTableView.js` so the menu and the table share ONE
 * width map — see that component's own docblock) and hands this component
 * the finished `{ fitColumnsToScreen, canFitColumns, alreadyFit }` from
 * `hooks/useFitColumnsToScreen.js`; this component decides nothing about
 * widths, same contract as every item below it.
 *
 * 🔑 THE ROWS ARE `list.rows` — the CURRENT TAB after the SEARCH, EVERY PAGE,
 * never `list.pageRows`: exactly what `DepartmentOrders.handleExport` sends
 * (its `hasFilters` ids = the filtered set, pagination ignored) and what its
 * `Print all` prints. The filter line on paper names the tab and the search, so
 * a sheet cannot pass for the whole department.
 *
 * 🟢 NO GATE, like Dept Orders' menu: every byte comes from rows this viewer is
 * already being shown (the export is client-side, `utils/hireListExport.js`).
 */
export function hireFilterSummary(tab, searchTerm) {
  const parts = [];
  if (tab && tab !== 'all') {
    const t = HIRE_TABLE_TABS.find((x) => x.id === tab);
    parts.push(`Tab: ${t ? t.label : tab}`);
  }
  const term = (searchTerm || '').trim();
  if (term) parts.push(`Search: "${term}"`);
  return parts.join(' · ');
}

export default function HireListActionsMenu({
  list, searchTerm, headerTitle, departmentName = '',
  // `Fit columns to screen` — the finished result of the page's own
  // `useFitColumnsToScreen()` call, or `null`/omitted on a page (or view)
  // that offers no width item at all. This component decides nothing about
  // widths; it just renders what it was handed, same as every other item.
  fit = null,
}) {
  const rows = list.rows;
  const narrowed = list.tab !== 'all' || (searchTerm || '').trim() !== '';
  return (
    <Dropdown
      align="right"
      haspopup="menu"
      panelClassName="w-64 p-1"
      triggerClassName={TOOLBAR_DISCLOSURE_TRIGGER}
      title="Page actions"
      ariaLabel="Page actions"
      triggerTestId="hires-actions-menu"
      label="Actions"
    >
      {({ close }) => (
        <div role="menu" aria-label="Page actions">
          {narrowed && (
            <p className="px-3 pt-2 pb-1 text-xs text-gray-500 dark:text-gray-400">
              Exporting filtered results ({rows.length} hires)
            </p>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={() => { close(); downloadHireExport(rows); }}
            title={`Download the hires in this tab and search as ${CSV_LABEL}`}
            className={MENU_ITEM_NEUTRAL}
            data-testid="hires-actions-export-csv"
          >
            {CSV_MENU_LABEL}
          </button>
          <ListPrintMenuItems
            renderPdfMakeDoc={() => buildHireListDoc({
              hires: rows,
              headerTitle,
              departmentName,
              filterSummary: hireFilterSummary(list.tab, searchTerm),
              totalCount: rows.length,
            })}
            filename="hires.pdf"
            title={headerTitle}
            rowNoun="hire"
            onDone={close}
            testId="hires-actions-print-all"
            downloadTestId="hires-actions-download-list"
          />
          {/* `Fit columns to screen` — table-gated utility last, same
              placement rule as the order pages' identical item. `fit` is
              `null` outside Table view (nothing to fit) or on a page that
              has not wired it. */}
          {fit && (
            <button
              type="button"
              role="menuitem"
              onClick={() => { close(); fit.fitColumnsToScreen(); }}
              disabled={!fit.canFitColumns || fit.alreadyFit}
              title={!fit.canFitColumns
                ? 'Nothing measured to fit to yet'
                : fit.alreadyFit
                  ? 'The columns already fit the screen'
                  : 'Fit every column to the available screen width'}
              className={MENU_ITEM_NEUTRAL}
              data-testid="hires-actions-fit-columns"
            >
              Fit columns to screen
            </button>
          )}
        </div>
      )}
    </Dropdown>
  );
}
